import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  Icon
} from '@chakra-ui/react'
import { FiArrowRight } from 'react-icons/fi'

interface CarouselSlide {
  id: string
  hook: string
  subtext: string
  accentColor: string
  bgGradient: string
}

const slides: CarouselSlide[] = [
  {
    id: 'struggle',
    hook: "Tired of chasing clients instead of helping them?",
    subtext: "You became a practitioner to change lives — not to learn marketing.",
    accentColor: 'orange.400',
    bgGradient: 'linear(to-br, orange.50, amber.50)'
  },
  {
    id: 'outcome',
    hook: "What if clients came to you?",
    subtext: "A steady stream of the right people, finding you when they need you most.",
    accentColor: 'brand.500',
    bgGradient: 'linear(to-br, brand.50, teal.50)'
  },
  {
    id: 'proof',
    hook: "5 new consultations booked in 2 weeks.",
    subtext: "That's what happens when your expertise finally gets seen by the right people.",
    accentColor: 'green.500',
    bgGradient: 'linear(to-br, green.50, emerald.50)'
  },
  {
    id: 'simplicity',
    hook: "You answer a few questions. We do the rest.",
    subtext: "No ad platforms. No funnels. No marketing courses. Just results.",
    accentColor: 'blue.500',
    bgGradient: 'linear(to-br, blue.50, cyan.50)'
  },
  {
    id: 'cta',
    hook: "Ready to be found?",
    subtext: "Start getting clients in minutes, not months.",
    accentColor: 'brand.600',
    bgGradient: 'linear(to-br, brand.50, blue.50)'
  }
]

// Minimal, abstract illustrations for each slide
const SlideIllustration: React.FC<{ slideId: string }> = ({ slideId }) => {
  const baseStyle = {
    position: 'absolute' as const,
    right: '24px',
    bottom: '60px',
    opacity: 0.15,
    pointerEvents: 'none' as const,
  }

  switch (slideId) {
    case 'struggle':
      // Scattered, chaotic circles representing overwhelm
      return (
        <Box {...baseStyle} w="120px" h="120px">
          <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="30" cy="25" r="12" stroke="#EA580C" strokeWidth="2" strokeDasharray="4 2" />
            <circle cx="85" cy="20" r="8" stroke="#EA580C" strokeWidth="2" />
            <circle cx="60" cy="55" r="15" stroke="#EA580C" strokeWidth="2" strokeDasharray="4 2" />
            <circle cx="25" cy="80" r="10" stroke="#EA580C" strokeWidth="2" />
            <circle cx="95" cy="70" r="14" stroke="#EA580C" strokeWidth="2" strokeDasharray="4 2" />
            <circle cx="55" cy="95" r="9" stroke="#EA580C" strokeWidth="2" />
            {/* Small figure in center looking overwhelmed */}
            <circle cx="60" cy="55" r="6" fill="#EA580C" opacity="0.3" />
            <path d="M54 48 L60 42 L66 48" stroke="#EA580C" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </Box>
      )

    case 'outcome':
      // Magnet/beacon with people flowing toward center
      return (
        <Box {...baseStyle} w="120px" h="120px">
          <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Central beacon */}
            <circle cx="60" cy="60" r="20" fill="#16A34A" opacity="0.2" />
            <circle cx="60" cy="60" r="12" fill="#16A34A" opacity="0.3" />
            <circle cx="60" cy="60" r="5" fill="#16A34A" opacity="0.5" />
            {/* Radiating rings */}
            <circle cx="60" cy="60" r="35" stroke="#16A34A" strokeWidth="1.5" opacity="0.4" />
            <circle cx="60" cy="60" r="50" stroke="#16A34A" strokeWidth="1" opacity="0.2" />
            {/* People dots moving toward center */}
            <circle cx="20" cy="40" r="4" fill="#16A34A" opacity="0.6" />
            <circle cx="95" cy="35" r="4" fill="#16A34A" opacity="0.6" />
            <circle cx="30" cy="90" r="4" fill="#16A34A" opacity="0.6" />
            <circle cx="100" cy="80" r="4" fill="#16A34A" opacity="0.6" />
            {/* Motion lines */}
            <path d="M24 42 L40 52" stroke="#16A34A" strokeWidth="1.5" strokeLinecap="round" opacity="0.4" />
            <path d="M91 38 L78 50" stroke="#16A34A" strokeWidth="1.5" strokeLinecap="round" opacity="0.4" />
            <path d="M34 87 L45 75" stroke="#16A34A" strokeWidth="1.5" strokeLinecap="round" opacity="0.4" />
            <path d="M96 77 L82 70" stroke="#16A34A" strokeWidth="1.5" strokeLinecap="round" opacity="0.4" />
          </svg>
        </Box>
      )

    case 'proof':
      // Calendar with checkmarks
      return (
        <Box {...baseStyle} w="120px" h="120px">
          <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Calendar outline */}
            <rect x="15" y="25" width="90" height="80" rx="8" stroke="#16A34A" strokeWidth="2" />
            <path d="M15 45 L105 45" stroke="#16A34A" strokeWidth="2" />
            {/* Calendar hooks */}
            <path d="M35 15 L35 35" stroke="#16A34A" strokeWidth="3" strokeLinecap="round" />
            <path d="M85 15 L85 35" stroke="#16A34A" strokeWidth="3" strokeLinecap="round" />
            {/* Checkmarks in calendar cells */}
            <path d="M30 60 L38 68 L50 52" stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M55 60 L63 68 L75 52" stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M80 60 L88 68 L100 52" stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.5" />
            <path d="M30 82 L38 90 L50 74" stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M55 82 L63 90 L75 74" stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            {/* Number 5 badge */}
            <circle cx="98" cy="25" r="14" fill="#16A34A" opacity="0.3" />
            <text x="98" y="30" textAnchor="middle" fill="#16A34A" fontSize="14" fontWeight="bold">5</text>
          </svg>
        </Box>
      )

    case 'simplicity':
      // Chat bubble → Arrow → Rocket (simple transformation)
      return (
        <Box {...baseStyle} w="140px" h="100px">
          <svg viewBox="0 0 140 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Chat bubble (input) */}
            <rect x="5" y="25" width="35" height="30" rx="6" stroke="#2563EB" strokeWidth="2" />
            <path d="M15 55 L20 65 L25 55" stroke="#2563EB" strokeWidth="2" strokeLinejoin="round" />
            <circle cx="15" cy="40" r="2" fill="#2563EB" />
            <circle cx="23" cy="40" r="2" fill="#2563EB" />
            <circle cx="31" cy="40" r="2" fill="#2563EB" />
            
            {/* Arrow */}
            <path d="M50 45 L75 45" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" />
            <path d="M68 38 L75 45 L68 52" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            
            {/* Rocket (output) */}
            <path d="M105 70 L105 35 C105 25 115 20 120 15 C125 20 135 25 135 35 L135 70" stroke="#2563EB" strokeWidth="2" strokeLinejoin="round" />
            <path d="M100 70 L105 60 L120 60 L135 60 L140 70" stroke="#2563EB" strokeWidth="2" strokeLinejoin="round" />
            {/* Rocket window */}
            <circle cx="120" cy="42" r="6" stroke="#2563EB" strokeWidth="2" />
            {/* Flames */}
            <path d="M110 72 L110 82 M120 72 L120 85 M130 72 L130 82" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
          </svg>
        </Box>
      )

    case 'cta':
      // Spotlight/beacon shining down
      return (
        <Box {...baseStyle} w="120px" h="120px">
          <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Spotlight source */}
            <ellipse cx="60" cy="15" rx="20" ry="8" fill="#16A34A" opacity="0.4" />
            {/* Light beam */}
            <path d="M40 15 L20 100 L100 100 L80 15" fill="#16A34A" opacity="0.15" />
            {/* Person in spotlight */}
            <circle cx="60" cy="85" r="10" fill="#16A34A" opacity="0.4" />
            <circle cx="60" cy="70" r="6" fill="#16A34A" opacity="0.4" />
            {/* Sparkles */}
            <path d="M30 50 L35 55 L30 60 L25 55 Z" fill="#16A34A" opacity="0.5" />
            <path d="M90 45 L95 50 L90 55 L85 50 Z" fill="#16A34A" opacity="0.5" />
            <path d="M75 70 L78 73 L75 76 L72 73 Z" fill="#16A34A" opacity="0.5" />
            <path d="M45 65 L48 68 L45 71 L42 68 Z" fill="#16A34A" opacity="0.5" />
          </svg>
        </Box>
      )

    default:
      return null
  }
}

export const HeroCarousel: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0)
  const [isPaused, setIsPaused] = useState(false)
  const [progress, setProgress] = useState(0)
  
  const SLIDE_DURATION = 4000
  const PROGRESS_INTERVAL = 50
  
  const goToSlide = useCallback((index: number) => {
    setActiveIndex(index)
    setProgress(0)
  }, [])
  
  useEffect(() => {
    if (isPaused) return
    
    const progressTimer = setInterval(() => {
      setProgress((prev) => {
        const next = prev + (PROGRESS_INTERVAL / SLIDE_DURATION) * 100
        if (next >= 100) {
          setActiveIndex((prevIdx) => (prevIdx + 1) % slides.length)
          return 0
        }
        return next
      })
    }, PROGRESS_INTERVAL)
    
    return () => clearInterval(progressTimer)
  }, [isPaused])
  
  useEffect(() => {
    setProgress(0)
  }, [activeIndex])
  
  const currentSlide = slides[activeIndex]
  
  return (
    <Box
      position="relative"
      w="full"
      maxW="480px"
      mx="auto"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <Box
        bgGradient={currentSlide.bgGradient}
        borderRadius="2xl"
        shadow="2xl"
        border="1px solid"
        borderColor="gray.100"
        overflow="hidden"
        position="relative"
        transition="all 0.3s ease"
      >
        {/* Accent bar */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          h="4px"
          bg={currentSlide.accentColor}
          opacity={0.9}
        />
        
        {/* Illustration */}
        <SlideIllustration slideId={currentSlide.id} />
        
        {/* Content */}
        <Box p={{ base: 8, md: 10 }} minH={{ base: '280px', md: '300px' }} position="relative" zIndex={1}>
          <VStack 
            gap={5} 
            align="start" 
            justify="center"
            h="full"
            maxW="85%"
          >
            <Text
              fontSize={{ base: '2xl', md: '3xl' }}
              fontWeight="bold"
              color="gray.900"
              lineHeight="1.2"
              letterSpacing="tight"
            >
              {currentSlide.hook}
            </Text>
            
            <Text
              fontSize={{ base: 'md', md: 'lg' }}
              color="gray.600"
              lineHeight="relaxed"
            >
              {currentSlide.subtext}
            </Text>
            
            {currentSlide.id === 'cta' && (
              <HStack 
                gap={2} 
                color="brand.600" 
                fontWeight="semibold"
                fontSize="lg"
                mt={2}
                _hover={{ gap: 3 }}
                transition="all 0.2s"
                cursor="pointer"
              >
                <Text>Get started free</Text>
                <Icon as={FiArrowRight} />
              </HStack>
            )}
          </VStack>
        </Box>
        
        {/* Progress indicators */}
        <Box px={{ base: 8, md: 10 }} pb={6}>
          <HStack gap={2} justify="center">
            {slides.map((slide, idx) => (
              <Box
                key={slide.id}
                as="button"
                onClick={() => goToSlide(idx)}
                h="3px"
                flex={1}
                maxW="60px"
                bg="gray.200"
                borderRadius="full"
                overflow="hidden"
                transition="all 0.2s"
                _hover={{ bg: 'gray.300' }}
              >
                <Box
                  h="full"
                  bg={idx === activeIndex ? currentSlide.accentColor : idx < activeIndex ? 'gray.400' : 'transparent'}
                  w={idx === activeIndex ? `${progress}%` : idx < activeIndex ? '100%' : '0%'}
                  borderRadius="full"
                  transition={idx === activeIndex ? 'none' : 'width 0.3s ease'}
                />
              </Box>
            ))}
          </HStack>
        </Box>
      </Box>
      
      {/* Slide counter */}
      <HStack justify="center" mt={4} gap={1} opacity={0.5}>
        <Text fontSize="xs" color="gray.500">
          {activeIndex + 1} of {slides.length}
        </Text>
      </HStack>
    </Box>
  )
}
